import { CategoryFilterViewport } from '@/composables/category/CategoryFilterViewport';
import type { CategoryFilterObject } from '@/types/generated';
import _ from 'lodash';
import { computed, toValue, type MaybeRefOrGetter } from 'vue';

const HIDDEN_FACET_ATTRIBUTES = ['categories.id', 'categories.name'];

const filterPanelsByViewport = (filters: CategoryFilterObject[], viewport: CategoryFilterViewport) =>
    filters.filter((filter: CategoryFilterObject) =>
        viewport === CategoryFilterViewport.Mobile ? filter.is_shown_on_mobile : filter.is_shown_on_desktop,
    );
// Independent of the viewport flags: a filter can be hidden in both filter panels
// and still be shown as pills above the results.
const filterHeaderPanels = (filters: CategoryFilterObject[]) => filters.filter((filter: CategoryFilterObject) => filter.show_above_results);

const filterPopularPanels = (filters: CategoryFilterObject[], hidePopularFilters: boolean) =>
    hidePopularFilters ? filters.filter((filter: CategoryFilterObject) => !filter.show_above_results && !filter.is_popular) : filters;

const isVisibleFilterPanel = (filter: CategoryFilterObject, availableFilterKeys: string[]) =>
    !_.includes(HIDDEN_FACET_ATTRIBUTES, filter.filter_identifier) &&
    (_.isEmpty(availableFilterKeys) || _.includes(availableFilterKeys, filter.filter_identifier));

const orderFilterPanelsByAvailableKeys = (filters: CategoryFilterObject[], availableFilterKeys: string[]): CategoryFilterObject[] => {
    const availableOrder = _.fromPairs(availableFilterKeys.map((key, index) => [key, index]));

    return _.orderBy(
        filters,
        [
            (filter: CategoryFilterObject) => availableOrder[filter.filter_identifier] ?? Number.MAX_SAFE_INTEGER,
            (filter: CategoryFilterObject) => filter.order ?? Number.MAX_SAFE_INTEGER,
            'filter_identifier',
        ],
        ['asc', 'asc', 'asc'],
    );
};

export function useCategorySearchPanels(
    filters: MaybeRefOrGetter<CategoryFilterObject[]>,
    availableFilters: MaybeRefOrGetter<string[]>,
    availablePageFilters: MaybeRefOrGetter<Record<string, Record<string, number>>>,
    hidePopularFiltersOnDesktop: MaybeRefOrGetter<boolean>,
    hidePopularFiltersOnMobile: MaybeRefOrGetter<boolean>,
    headerFiltersCount: MaybeRefOrGetter<number | null>,
) {
    const availableFilterKeys = computed(() => _.uniq([...toValue(availableFilters), ...Object.keys(toValue(availablePageFilters))]));

    const visibleFilterPanels = computed<CategoryFilterObject[]>(() =>
        toValue(filters).filter((filter: CategoryFilterObject) => isVisibleFilterPanel(filter, availableFilterKeys.value)),
    );

    const desktopFilterPanels = computed(() =>
        filterPopularPanels(filterPanelsByViewport(visibleFilterPanels.value, CategoryFilterViewport.Desktop), toValue(hidePopularFiltersOnDesktop)),
    );

    const mobileFilterPanels = computed(() =>
        filterPopularPanels(filterPanelsByViewport(visibleFilterPanels.value, CategoryFilterViewport.Mobile), toValue(hidePopularFiltersOnMobile)),
    );
    const headerFilterPanels = computed(() => {
        const explicitHeaderPanels = filterHeaderPanels(visibleFilterPanels.value);
        if (explicitHeaderPanels.length > 0) {
            return explicitHeaderPanels;
        }

        const configuredHeaderFiltersCount = toValue(headerFiltersCount);
        if (configuredHeaderFiltersCount === null || configuredHeaderFiltersCount <= 0) {
            return [];
        }

        return orderFilterPanelsByAvailableKeys(visibleFilterPanels.value, availableFilterKeys.value).slice(0, configuredHeaderFiltersCount);
    });

    return {
        availableFilterKeys,
        visibleFilterPanels,
        desktopFilterPanels,
        mobileFilterPanels,
        headerFilterPanels,
    };
}
